import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import {
  createSystemAccount,
  getSystemAccountDetail,
  listSystemAccounts,
  sendSystemAccountBatchMessage,
  sendSystemAccountMessage,
  setSystemAccountEnabled,
  updateSystemAccount,
  type Application,
  type PageResult,
  type SystemAccount,
  type SystemAccountAudit,
  type SystemAccountDraft,
  type SystemAccountMessageType,
} from "../api";
import { Button, IconButton } from "./ui/Button";
import { Dialog } from "./ui/Dialog";
import { Icon } from "./ui/Icon";
import {
  CopyText,
  ManagementHeading,
  PageState,
  PaginationControls,
  TimeValue,
  UserIdentity,
} from "./ManagementCommon";

const ALL_MESSAGE_TYPES: readonly SystemAccountMessageType[] = [
  "text", "image", "audio", "video", "file", "custom", "command",
];

const EMPTY_DRAFT: SystemAccountDraft = {
  systemUserId: "",
  displayName: "",
  avatarUrl: null,
  description: "",
  isDefault: false,
  replyPolicy: "DISABLED",
  allowedMessageTypes: [...ALL_MESSAGE_TYPES],
  rateLimitPerSecond: 20,
  rateLimitPerDay: 100_000,
};

/** Application account-management page for non-login server-side identities. */
export function SystemAccountManagementView({ application, applications, onSelect, onNotice, onError }: {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly onSelect: (application: Application) => void;
  readonly onNotice: (message: string) => void;
  readonly onError: (message: string) => void;
}) {
  const [draftKeyword, setDraftKeyword] = useState("");
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [result, setResult] = useState<PageResult<SystemAccount> | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SystemAccount | "create" | null>(null);
  const [sending, setSending] = useState<SystemAccount | null>(null);
  const [detail, setDetail] = useState<{ account: SystemAccount; audits: readonly SystemAccountAudit[] } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => { setReloadKey((value) => value + 1); }, []);

  useEffect(() => {
    setDraftKeyword(""); setKeyword(""); setStatus(""); setPageNo(1);
    setResult(null); setLoadError(null); setDetail(null);
  }, [application?.appId]);

  useEffect(() => {
    if (application === null) return;
    let active = true;
    setLoading(true); setLoadError(null);
    void listSystemAccounts(application.appId, { pageNo, pageSize: 20, keyword, status })
      .then((value) => { if (active) setResult(value); })
      .catch((cause: unknown) => { if (active) setLoadError(message(cause)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [application?.appId, keyword, pageNo, reloadKey, status]);

  async function openDetail(account: SystemAccount): Promise<void> {
    if (application === null) return;
    try {
      const value = await getSystemAccountDetail(application.appId, account.systemUserId);
      setDetail({ account: value.systemAccount, audits: value.audits });
    } catch (cause) { onError(message(cause)); }
  }

  async function toggle(account: SystemAccount): Promise<void> {
    if (application === null) return;
    const enable = account.status !== "ENABLED";
    const confirmed = window.confirm(
      enable
        ? `确认重新启用系统账号 ${account.systemUserId}？`
        : `禁用后该账号不能发送新消息，历史消息不受影响。确认禁用 ${account.systemUserId}？`,
    );
    if (!confirmed) return;
    try {
      await setSystemAccountEnabled(application.appId, account, enable);
      onNotice(`系统账号 ${account.systemUserId} 已${enable ? "启用" : "禁用"}`);
      setDetail(null); reload();
    } catch (cause) { onError(message(cause)); }
  }

  const canCreate = application?.permissions.includes("im.system_account.create") === true;
  const canUpdate = application?.permissions.includes("im.system_account.update") === true;
  const canEnable = application?.permissions.includes("im.system_account.enable") === true;
  const canDisable = application?.permissions.includes("im.system_account.disable") === true;
  const canSend = application?.permissions.includes("im.system_account.send") === true;
  return <div className="management-page">
    <ManagementHeading
      title="系统账号"
      description="配置仅供可信服务端发送单聊、群聊及广播消息的应用级身份；系统账号不能登录客户端。"
      applications={applications}
      selected={application}
      onSelect={onSelect}
      action={canCreate ? <Button leadingIcon="plus" variant="primary" onClick={() => { setEditing("create"); }}>创建系统账号</Button> : undefined}
    />
    {application === null ? <div className="empty-page"><strong>请先选择应用</strong><p>系统账号严格归属于一个应用。</p></div> :
      <section className="data-card management-card">
        <header className="data-card-header management-toolbar">
          <div><h3>系统账号列表</h3><p>{result === null ? "应用级服务身份" : `共 ${result.total} 个系统账号`}</p></div>
          <form className="table-toolbar" onSubmit={(event) => { event.preventDefault(); setKeyword(draftKeyword.trim()); setPageNo(1); }}>
            <label className="search-control"><Icon name="search" size={17} /><span className="visually-hidden">搜索系统账号</span><input maxLength={64} placeholder="账号 ID 或显示名称" value={draftKeyword} onChange={(event) => { setDraftKeyword(event.currentTarget.value); }} /></label>
            <label className="select-control"><span className="visually-hidden">账号状态</span><select value={status} onChange={(event) => { setStatus(event.currentTarget.value); setPageNo(1); }}><option value="">全部状态</option><option value="ENABLED">已启用</option><option value="DISABLED">已禁用</option></select><Icon name="chevron-down" size={15} /></label>
            <Button size="small" type="submit">搜索</Button>
            <Button size="small" variant="ghost" type="button" onClick={() => { setDraftKeyword(""); setKeyword(""); setStatus(""); setPageNo(1); }}>重置</Button>
            <Button size="small" variant="ghost" type="button" onClick={reload}>刷新</Button>
          </form>
        </header>
        <PageState loading={loading} error={loadError} empty={result?.items.length === 0} filtered={keyword.length > 0 || status.length > 0} noun="系统账号" onRetry={reload}>
          <div className="table-container"><table className="management-table system-account-table"><thead><tr><th>账号</th><th>系统账号 ID</th><th>状态</th><th>回复策略</th><th>今日发送</th><th>更新时间</th><th>操作</th></tr></thead><tbody>{result?.items.map((account) => <tr key={account.systemUserId}><td><UserIdentity userId={account.systemUserId} displayName={account.displayName} avatarUrl={account.avatarUrl} />{account.isDefault ? <span className="management-tag tag-info">默认</span> : null}</td><td><CopyText label="系统账号 ID" value={account.systemUserId} /></td><td><AccountStatus status={account.status} /></td><td><span className="management-tag tag-muted">禁止回复</span></td><td>{account.sentToday.toLocaleString()}</td><td><TimeValue value={account.updatedAt} /></td><td><div className="table-actions">{canSend && account.status === "ENABLED" ? <Button size="small" variant="primary" onClick={() => { setSending(account); }}>发送消息</Button> : null}<Button size="small" variant="ghost" onClick={() => { void openDetail(account); }}>详情</Button>{canUpdate ? <Button size="small" variant="ghost" onClick={() => { setEditing(account); }}>编辑</Button> : null}{account.status === "ENABLED" ? (canDisable ? <Button size="small" variant="ghost" onClick={() => { void toggle(account); }}>禁用</Button> : null) : (canEnable ? <Button size="small" variant="ghost" onClick={() => { void toggle(account); }}>启用</Button> : null)}</div></td></tr>)}</tbody></table></div>
        </PageState>
        {result === null ? null : <PaginationControls {...result} onChange={setPageNo} />}
      </section>}
    {editing !== null && application !== null ? <AccountDialog application={application} account={editing === "create" ? null : editing} onClose={() => { setEditing(null); }} onSaved={(account) => { setEditing(null); onNotice(`系统账号 ${account.systemUserId} 已保存`); reload(); }} onError={onError} /> : null}
    {sending !== null && application !== null ? <MessageDialog application={application} account={sending} onClose={() => { setSending(null); }} onSent={(notice) => { setSending(null); onNotice(notice); reload(); }} onError={onError} /> : null}
    {detail === null ? null : <AccountDetail value={detail} canEdit={canUpdate} onClose={() => { setDetail(null); }} onEdit={() => { setEditing(detail.account); setDetail(null); }} />}
  </div>;
}

function MessageDialog({ application, account, onClose, onSent, onError }: {
  readonly application: Application;
  readonly account: SystemAccount;
  readonly onClose: () => void;
  readonly onSent: (notice: string) => void;
  readonly onError: (message: string) => void;
}) {
  const [targetType, setTargetType] = useState<"USER" | "GROUP" | "GROUP_USERS">("USER");
  const [targetId, setTargetId] = useState("");
  const [batchUserIds, setBatchUserIds] = useState("");
  const [messageType, setMessageType] = useState<SystemAccountMessageType>(
    account.allowedMessageTypes[0] ?? "text",
  );
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const normalizedTargetId = targetId.trim();
    const normalizedContent = content.trim();
    if (normalizedTargetId.length === 0) {
      onError(targetType === "USER" ? "IM 用户 ID 不能为空" : "群组 ID 不能为空");
      return;
    }
    if (normalizedContent.length === 0) {
      onError(messageType === "text" ? "消息内容不能为空" : "Payload 不能为空");
      return;
    }
    const recipientUserIds = [...new Set(batchUserIds
      .split(/[,，;；\s]+/u)
      .map((value) => value.trim())
      .filter((value) => value.length > 0))];
    if (targetType === "GROUP_USERS" && recipientUserIds.length === 0) {
      onError("请至少填写一个群成员用户 ID");
      return;
    }
    if (recipientUserIds.length > 100) {
      onError("单次最多向 100 个群成员批量推送");
      return;
    }
    if (recipientUserIds.some((userId) => userId.length > 128)) {
      onError("群成员用户 ID 不能超过 128 个字符");
      return;
    }
    let messagePayload: unknown;
    if (messageType !== "text") {
      try {
        messagePayload = JSON.parse(normalizedContent) as unknown;
      } catch {
        onError("非文本消息的 Payload 必须是有效 JSON");
        return;
      }
    }
    setBusy(true);
    try {
      const outgoingMessage = messageType === "text"
        ? { type: "TEXT" as const, text: content }
        : { type: serverMessageType(messageType), payload: messagePayload };
      if (targetType === "GROUP_USERS") {
        const ack = await sendSystemAccountBatchMessage(application.appId, account.systemUserId, {
          to: { type: "GROUP_USERS", groupId: normalizedTargetId, userIds: recipientUserIds },
          message: outgoingMessage,
        });
        onSent(`批量消息已发送给 ${ack.recipientCount} 位群成员，批次 ID ${ack.batchId}`);
      } else {
        const ack = await sendSystemAccountMessage(application.appId, account.systemUserId, {
          to: targetType === "USER"
            ? { type: "USER", userId: normalizedTargetId }
            : { type: "GROUP", groupId: normalizedTargetId },
          message: outgoingMessage,
        });
        onSent(`消息已发送，序号 ${ack.seq}，消息 ID ${ack.messageId}`);
      }
    } catch (cause) {
      onError(message(cause));
    } finally {
      setBusy(false);
    }
  }

  return <Dialog busy={busy} description="消息经过 Conversation DO 排序、去重和可靠写入后才返回成功。批量模式先校验群成员，再分别进入系统账号单聊。" eyebrow={`${application.name} · ${account.displayName}`} size="large" title="使用系统账号发送消息" onClose={onClose}><form className="dialog-form" onSubmit={(event) => { void submit(event); }}><div className="form-grid">
    <label className="field"><span>发送目标 <em>*</em></span><select value={targetType} onChange={(event) => { setTargetType(event.currentTarget.value as "USER" | "GROUP" | "GROUP_USERS"); }}><option value="USER">指定 IM 用户</option><option value="GROUP">指定群组 / 广播</option><option value="GROUP_USERS">群内指定用户（批量单聊）</option></select></label>
    <label className="field"><span>{targetType === "USER" ? "IM 用户 ID" : "群组 ID"} <em>*</em></span><input autoFocus required maxLength={128} placeholder={targetType === "USER" ? "例如 u123456" : "例如 group_xxx 或 broadcast_xxx"} value={targetId} onChange={(event) => { setTargetId(event.currentTarget.value); }} /></label>
    {targetType === "GROUP_USERS" ? <label className="field field-full"><span>群成员用户 ID <em>*</em></span><textarea required rows={4} placeholder="每行一个用户 ID，也支持逗号或空格分隔；单次最多 100 人" value={batchUserIds} onChange={(event) => { setBatchUserIds(event.currentTarget.value); }} /><small>所有用户必须是目标群当前的活跃成员；每人会收到一条独立、可安全重试的系统账号单聊。</small></label> : null}
    <label className="field field-full"><span>消息类型 <em>*</em></span><select value={messageType} onChange={(event) => { setMessageType(event.currentTarget.value as SystemAccountMessageType); setContent(""); }}>{account.allowedMessageTypes.map((type) => <option key={type} value={type}>{messageTypeLabel(type)}</option>)}</select><small>仅展示当前系统账号配置允许发送的消息类型。</small></label>
    <label className="field field-full"><span>{messageType === "text" ? "消息内容" : "Payload（JSON）"} <em>*</em></span><textarea required rows={8} maxLength={32 * 1024} placeholder={messageType === "text" ? "请输入要发送的消息" : payloadPlaceholder(messageType)} value={content} onChange={(event) => { setContent(event.currentTarget.value); }} /><small>{targetType === "GROUP" ? "普通群会形成一条群消息；广播类型会话还会携带 isBroadcast 标记。" : targetType === "GROUP_USERS" ? "批量消息不会写入群历史，也不会改变群会话 seq。" : "系统账号会自动建立或复用与该用户的稳定 P2P 会话。"}</small></label>
  </div><footer className="dialog-footer"><Button disabled={busy} type="button" onClick={onClose}>取消</Button><Button loading={busy} type="submit" variant="primary">确认发送</Button></footer></form></Dialog>;
}

function messageTypeLabel(type: SystemAccountMessageType): string {
  return ({ text: "文本", image: "图片", audio: "语音", video: "视频", file: "文件", custom: "自定义", command: "CMD 指令" })[type];
}

function serverMessageType(type: SystemAccountMessageType): Uppercase<SystemAccountMessageType> {
  const values: Record<SystemAccountMessageType, Uppercase<SystemAccountMessageType>> = {
    text: "TEXT",
    image: "IMAGE",
    audio: "AUDIO",
    video: "VIDEO",
    file: "FILE",
    custom: "CUSTOM",
    command: "COMMAND",
  };
  return values[type];
}

function payloadPlaceholder(type: SystemAccountMessageType): string {
  switch (type) {
    case "image":
      return '{"uuid":"image-001","format":"jpeg","original":{"objectKey":"media/v2/...","sizeBytes":1024,"width":800,"height":600}}';
    case "audio":
      return '{"uuid":"audio-001","source":{"objectKey":"media/v2/...","sizeBytes":1024},"durationMs":1000,"codec":"opus"}';
    case "video":
      return '{"version":1,"kind":"video","uuid":"video-001","source":{"objectKey":"media/v2/...","sizeBytes":1024},"durationMs":1000,"width":1280,"height":720}';
    case "file":
      return '{"version":1,"kind":"file","uuid":"file-001","source":{"objectKey":"media/v2/...","sizeBytes":1024},"fileName":"document.pdf","extension":"pdf"}';
    case "custom":
      return '{"data":{"key":"value"},"description":"业务通知","extension":"notice-v1"}';
    case "command":
      return '{"name":"refresh","parameters":{},"requireAck":false,"priority":"low"}';
    case "text":
      return "请输入要发送的消息";
  }
}

function AccountDialog({ application, account, onClose, onSaved, onError }: {
  readonly application: Application;
  readonly account: SystemAccount | null;
  readonly onClose: () => void;
  readonly onSaved: (account: SystemAccount) => void;
  readonly onError: (message: string) => void;
}) {
  const [draft, setDraft] = useState<SystemAccountDraft>(account === null ? EMPTY_DRAFT : {
    systemUserId: account.systemUserId,
    displayName: account.displayName,
    avatarUrl: account.avatarUrl,
    description: account.description,
    isDefault: account.isDefault,
    replyPolicy: account.replyPolicy,
    allowedMessageTypes: account.allowedMessageTypes,
    rateLimitPerSecond: account.rateLimitPerSecond,
    rateLimitPerDay: account.rateLimitPerDay,
  });
  const [busy, setBusy] = useState(false);
  async function submit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault(); setBusy(true);
    try {
      const result = account === null
        ? await createSystemAccount(application.appId, draft)
        : await updateSystemAccount(application.appId, account, draft);
      onSaved(result.systemAccount);
    } catch (cause) { onError(message(cause)); }
    finally { setBusy(false); }
  }
  function toggleType(type: SystemAccountMessageType): void {
    const selected = draft.allowedMessageTypes.includes(type);
    const next = selected ? draft.allowedMessageTypes.filter((item) => item !== type) : [...draft.allowedMessageTypes, type];
    if (next.length > 0) setDraft({ ...draft, allowedMessageTypes: next });
  }
  return <Dialog busy={busy} description="系统账号只能通过服务端 API 发送消息，不签发客户端登录态。" eyebrow={application.name} title={account === null ? "创建系统账号" : "编辑系统账号"} onClose={onClose}><form className="dialog-form" onSubmit={(event) => { void submit(event); }}><div className="form-grid">
    <label className="field field-full"><span>系统账号 ID <em>*</em></span><input disabled={account !== null} required maxLength={128} pattern="[A-Za-z0-9][A-Za-z0-9._:@-]*" value={draft.systemUserId} onChange={(event) => { setDraft({ ...draft, systemUserId: event.currentTarget.value }); }} /><small>应用内唯一，创建后不可修改。</small></label>
    <label className="field field-full"><span>显示名称 <em>*</em></span><input required maxLength={256} value={draft.displayName} onChange={(event) => { setDraft({ ...draft, displayName: event.currentTarget.value }); }} /></label>
    <label className="field field-full"><span>头像 URL</span><input type="url" maxLength={2048} placeholder="https://example.com/avatar.png" value={draft.avatarUrl ?? ""} onChange={(event) => { setDraft({ ...draft, avatarUrl: event.currentTarget.value || null }); }} /></label>
    <label className="field field-full"><span>账号说明</span><textarea maxLength={1000} rows={3} value={draft.description} onChange={(event) => { setDraft({ ...draft, description: event.currentTarget.value }); }} /></label>
    <label className="field"><span>每秒发送上限</span><input min={1} max={1000} required type="number" value={draft.rateLimitPerSecond} onChange={(event) => { setDraft({ ...draft, rateLimitPerSecond: Number(event.currentTarget.value) }); }} /></label>
    <label className="field"><span>每日发送上限</span><input min={1} max={10000000} required type="number" value={draft.rateLimitPerDay} onChange={(event) => { setDraft({ ...draft, rateLimitPerDay: Number(event.currentTarget.value) }); }} /></label>
    <fieldset className="field field-full message-type-field"><legend>允许发送的消息类型</legend><div className="checkbox-grid">{ALL_MESSAGE_TYPES.map((type) => <label key={type}><input checked={draft.allowedMessageTypes.includes(type)} type="checkbox" onChange={() => { toggleType(type); }} /><span>{type}</span></label>)}</div></fieldset>
    <label className="checkbox-field field-full"><input checked={draft.isDefault} type="checkbox" onChange={(event) => { setDraft({ ...draft, isDefault: event.currentTarget.checked }); }} /><span>设为当前应用的默认系统账号</span></label>
  </div><footer className="dialog-footer"><Button disabled={busy} type="button" onClick={onClose}>取消</Button><Button loading={busy} type="submit" variant="primary">保存账号</Button></footer></form></Dialog>;
}

function AccountDetail({ value, canEdit, onClose, onEdit }: {
  readonly value: { readonly account: SystemAccount; readonly audits: readonly SystemAccountAudit[] };
  readonly canEdit: boolean;
  readonly onClose: () => void;
  readonly onEdit: () => void;
}) {
  const account = value.account;
  return <div className="drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><aside aria-label="系统账号详情" className="detail-drawer"><header className="drawer-header"><div><p className="eyebrow">SYSTEM ACCOUNT</p><h2>{account.displayName}</h2></div><IconButton icon="x" label="关闭" onClick={onClose} /></header><div className="drawer-body"><section className="detail-section"><div className="section-heading"><div><h3>账号配置</h3><p>创建后 ID 不可修改</p></div><AccountStatus status={account.status} /></div><dl className="message-definition"><Detail label="系统账号 ID" value={account.systemUserId} /><Detail label="默认账号" value={account.isDefault ? "是" : "否"} /><Detail label="回复策略" value="禁止回复" /><Detail label="允许类型" value={account.allowedMessageTypes.join(", ")} /><Detail label="每秒限额" value={String(account.rateLimitPerSecond)} /><Detail label="每日限额" value={String(account.rateLimitPerDay)} /><Detail label="今日发送" value={String(account.sentToday)} /><Detail label="版本" value={`v${account.version}`} /></dl></section><section className="detail-section"><div className="section-heading"><div><h3>最近操作</h3><p>仅展示必要配置审计，不含消息正文</p></div></div>{value.audits.length === 0 ? <p className="muted-value">暂无审计记录</p> : <ol className="audit-list">{value.audits.map((audit) => <li key={audit.auditId}><strong>{audit.operation}</strong><span>{audit.operatorId}</span><TimeValue value={audit.createdAt} /><small>Request ID: {audit.requestId}</small></li>)}</ol>}</section></div><footer className="drawer-footer"><Button onClick={onClose}>关闭</Button>{canEdit ? <Button variant="primary" onClick={onEdit}>编辑账号</Button> : null}</footer></aside></div>;
}

function AccountStatus({ status }: { readonly status: SystemAccount["status"] }) {
  return <span className={`management-tag ${status === "ENABLED" ? "tag-success" : "tag-danger"}`}>{status === "ENABLED" ? "已启用" : "已禁用"}</span>;
}

function Detail({ label, value }: { readonly label: string; readonly value: string }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function message(cause: unknown): string { return cause instanceof Error ? cause.message : String(cause); }
